import React, { useState, useEffect, useMemo } from 'react';
import { Table, Badge, Card } from 'react-bootstrap';
import PhoenixDocCard from 'components/base/PhoenixDocCard';
import { useSelector } from 'react-redux';
import { selectProjectResults } from '../data-access/project.reducer';
import { pickGovernanceEntry } from '../../shared/governanceResultsAccess';

interface InternalComplianceItem {
  dimension: string;
  issue_type: string;
  severity: string;
  feature: string;
  model: string;
  evidence: string;
  provenance: string;
  status: string;
  internal_clause: string;
  similarity: number;
  reason: string;
}

interface InternalComplianceTabProps {
  projectId: string;
  userId: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const InternalComplianceTab = (_props: InternalComplianceTabProps) => {
  const projectResults = useSelector(selectProjectResults);
  const [data, setData] = useState<any | null>(null);

  const internalComplianceData = useMemo(
    () => pickGovernanceEntry(projectResults, ['internal_compliance']),
    [projectResults]
  );

  useEffect(() => {
    setData(internalComplianceData ?? null);
  }, [internalComplianceData]);

  const getSeverityBadge = (severity: string) => {
    switch (severity?.toUpperCase()) {
      case 'HIGH':
        return <Badge bg="danger">HIGH</Badge>;
      case 'MEDIUM':
        return (
          <Badge bg="warning" text="dark">
            MEDIUM
          </Badge>
        );
      case 'LOW':
        return <Badge bg="info">LOW</Badge>;
      default:
        return <Badge bg="secondary">{severity}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    return status === 'OK' ? <Badge bg="success">PASS</Badge> : <Badge bg="danger">FAIL</Badge>;
  };

  const complianceItems: InternalComplianceItem[] = data?.internal_compliance || [];

  return (
    <div className="mb-4">
      <PhoenixDocCard className="mb-4">
        <PhoenixDocCard.Header title="Internal Compliance Analysis">
          <p className="text-muted mb-0">Internal policy compliance analysis based on project results.</p>
        </PhoenixDocCard.Header>
        <PhoenixDocCard.Body>
          {data && (
            <div className="mt-4">
              <div className="d-flex align-items-center gap-4 mb-4 p-3 bg-light rounded border">
                <div>
                  <h5 className="mb-1">Overall Status</h5>
                  {getStatusBadge(data.overall_internal_status || 'UNKNOWN')}
                </div>
                {data.title && (
                  <div>
                    <h5 className="mb-1">Title</h5>
                    <span className="fw-bold text-primary">{data.title}</span>
                  </div>
                )}
                {data.timestamp && (
                  <div>
                    <h5 className="mb-1">Generated At</h5>
                    <span className="text-muted">
                      {new Date(data.timestamp.$date || data.timestamp).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>

              {complianceItems.length > 0 && (
                <>
                  <h5 className="mb-3">Compliance Issues</h5>
                  <div className="table-responsive">
                    <Table bordered hover className="align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ width: '15%' }}>Dimension</th>
                          <th style={{ width: '15%' }}>Issue Type</th>
                          <th style={{ width: '10%' }}>Severity</th>
                          <th style={{ width: '10%' }}>Status</th>
                          <th style={{ width: '15%' }}>Feature/Model</th>
                          <th style={{ width: '15%' }}>Evidence</th>
                          <th style={{ width: '20%' }}>Internal Clause</th>
                        </tr>
                      </thead>
                      <tbody>
                        {complianceItems.map((item: InternalComplianceItem, idx: number) => (
                          <tr key={idx}>
                            <td className="fw-semibold">{item.dimension}</td>
                            <td>{item.issue_type}</td>
                            <td>{getSeverityBadge(item.severity)}</td>
                            <td>{getStatusBadge(item.status)}</td>
                            <td>
                              <div className="small">
                                <div className="fw-bold">{item.feature}</div>
                                <div className="text-muted">{item.model}</div>
                              </div>
                            </td>
                            <td>
                              <small className="text-muted">{item.evidence}</small>
                            </td>
                            <td>
                              <small className="text-muted">
                                {item.internal_clause?.substring(0, 100)}
                                {item.internal_clause?.length > 100 && '...'}
                              </small>
                              {item.similarity && (
                                <div className="mt-1">
                                  <Badge bg="secondary" className="small">
                                    Similarity: {(item.similarity * 100).toFixed(1)}%
                                  </Badge>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>

                  {complianceItems.some((item: InternalComplianceItem) => item.reason) && (
                    <div className="mt-4">
                      <h5 className="mb-3">Detailed Reasons</h5>
                      <Card className="bg-light border-0">
                        <Card.Body>
                          {complianceItems.map(
                            (item: InternalComplianceItem, idx: number) =>
                              item.reason && (
                                <div key={idx} className="mb-3">
                                  <div className="fw-bold mb-1">{item.issue_type}</div>
                                  <div className="small text-muted">{item.reason}</div>
                                </div>
                              )
                          )}
                        </Card.Body>
                      </Card>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {!data && (
            <div className="text-center py-5 text-muted">
              <h5>No Analysis Data</h5>
              <p>No Internal Compliance analysis data available for this project.</p>
            </div>
          )}
        </PhoenixDocCard.Body>
      </PhoenixDocCard>
    </div>
  );
};

export default InternalComplianceTab;
